import type { DecorationType } from '../types/document'
import { BULB_COLORS } from './palettes'

/**
 * Real product, photographed.
 *
 * Three passes at drawing a wreath in code established the ceiling: it went
 * from clip art to a decent illustration and stopped. Foliage is translucent,
 * has depth of field and a thousand small shadows, and none of that survives
 * being stroked onto a canvas. So the greenery is a picture of the real
 * product and the code keeps the parts that have to change per customer.
 *
 * The greenery is green in every one of these. "Warm white" and "pure white"
 * describe the BULBS, not the branches; there is no flocked or snowy product
 * in the line.
 *
 * Every piece carries its real world size, which is what makes these more than
 * decoration. Once a photo is calibrated a 26 inch wreath places at 26 inches
 * with nobody touching a slider, and a garland run reports the linear feet it
 * actually covers straight onto the quote.
 */
export interface DecorStyle {
  id: string
  kind: DecorationType
  /** Bulb color, which is how these are chosen and sold. */
  label: string
  /** Served from public/, so artwork stays out of the JS bundle. */
  url: string
  /** The artwork already contains its own lit bulbs. */
  lit: boolean
  aspect: number
  /** Real size of the piece along its long axis, in feet. Drives the quote. */
  feet: number
  segment?: 'top' | 'left' | 'right'
  tiles?: boolean
  /**
   * The lamp colors in this photo. Used when extra bulbs are added on top,
   * so they match the ones already in the picture instead of announcing
   * themselves as a second set.
   */
  bulbPalette?: string[]
}

const GARLAND_TOP_FT = 8
const GARLAND_LEG_FT = 7
const WREATH_FT = 26 / 12
const BOW_FT = 14 / 12

/**
 * Garland ships as three pieces rather than one arch, which is how a garage
 * door actually gets framed: a run across the top and a leg down each side,
 * overlapped at the corners so they merge. Straight segments also tile for a
 * long run, where one stretched arch would smear.
 */
export const DECOR_STYLES: DecorStyle[] = [
  { id: 'garland-warm-top',    kind: 'garland', label: 'Warm white', url: 'decor/garland-warm-top.webp',    lit: true, aspect: 1100 / 393,  feet: GARLAND_TOP_FT, segment: 'top',   tiles: true, bulbPalette: [BULB_COLORS.warmWhite] },
  { id: 'garland-warm-left',   kind: 'garland', label: 'Warm white', url: 'decor/garland-warm-left.webp',   lit: true, aspect: 405 / 1041,  feet: GARLAND_LEG_FT, segment: 'left',  tiles: true, bulbPalette: [BULB_COLORS.warmWhite] },
  { id: 'garland-warm-right',  kind: 'garland', label: 'Warm white', url: 'decor/garland-warm-right.webp',  lit: true, aspect: 407 / 1044,  feet: GARLAND_LEG_FT, segment: 'right', tiles: true, bulbPalette: [BULB_COLORS.warmWhite] },
  { id: 'garland-white-top',   kind: 'garland', label: 'Pure white', url: 'decor/garland-white-top.webp',   lit: true, aspect: 1100 / 391,  feet: GARLAND_TOP_FT, segment: 'top',   tiles: true, bulbPalette: [BULB_COLORS.pureWhite] },
  { id: 'garland-white-left',  kind: 'garland', label: 'Pure white', url: 'decor/garland-white-left.webp',  lit: true, aspect: 391 / 993,  feet: GARLAND_LEG_FT, segment: 'left',  tiles: true, bulbPalette: [BULB_COLORS.pureWhite] },
  { id: 'garland-white-right', kind: 'garland', label: 'Pure white', url: 'decor/garland-white-right.webp', lit: true, aspect: 409 / 1020,  feet: GARLAND_LEG_FT, segment: 'right', tiles: true, bulbPalette: [BULB_COLORS.pureWhite] },

  { id: 'wreath-warm-white', kind: 'wreath', label: 'Warm white', url: 'decor/wreath-warm-white.webp', lit: true, aspect: 1100 / 1093, feet: WREATH_FT, bulbPalette: [BULB_COLORS.warmWhite] },
  { id: 'wreath-pure-white', kind: 'wreath', label: 'Pure white', url: 'decor/wreath-pure-white.webp', lit: true, aspect: 1100 / 1079, feet: WREATH_FT, bulbPalette: [BULB_COLORS.pureWhite] },
  { id: 'wreath-multicolor', kind: 'wreath', label: 'Multicolor', url: 'decor/wreath-multicolor.webp', lit: true, aspect: 1094 / 1100, feet: WREATH_FT, bulbPalette: [BULB_COLORS.red, BULB_COLORS.green, BULB_COLORS.blue, BULB_COLORS.amber] },

  // The bow is unlit and goes on a wreath or on its own, so it rotates freely.
  { id: 'bow-red', kind: 'bow', label: 'Red bow', url: 'decor/bow-red.webp', lit: false, aspect: 904 / 1100, feet: BOW_FT },
]

export const stylesFor = (kind: DecorationType) => DECOR_STYLES.filter((s) => s.kind === kind)

/**
 * Everything that is simply placed rather than run along a line. Wreaths have
 * their own category because they are half the business; this is the rest -
 * the bow today, and whatever gets photographed next. Adding a reindeer means
 * adding one row above, not touching the interface.
 */
/**
 * WHAT A CATEGORY PUTS DOWN, if it puts anything down.
 *
 * Empty for every category that draws a strand, which is how the panel knows
 * to show bulb settings instead. One place, so adding a product is still one
 * row above and nothing else.
 */
export function placeableFor(category: string): DecorStyle[] {
  if (category === 'wreaths') return stylesFor('wreath')
  if (category === 'accessories') return accessoryStyles()
  return []
}

export const accessoryStyles = () =>
  DECOR_STYLES.filter((s) => s.kind !== 'wreath' && s.kind !== 'garland')

/** The bulb colors garland comes in. One entry per color, not per segment. */
export const garlandColors = () =>
  DECOR_STYLES.filter((s) => s.kind === 'garland' && s.segment === 'top')

export const garlandSegment = (label: string, segment: 'top' | 'left' | 'right') =>
  DECOR_STYLES.find((s) => s.kind === 'garland' && s.label === label && s.segment === segment)

export const styleById = (id: string | undefined) =>
  id ? DECOR_STYLES.find((s) => s.id === id) : undefined

/**
 * The product to use when nobody has picked one.
 *
 * Without this a wreath placed before visiting the product picker had no style
 * at all, so the renderer found no artwork and quietly fell back to drawing one
 * by hand. The drawn wreath is a filled disc - it has no hole - which is why a
 * placed wreath came out as a solid green starburst instead of the photograph
 * sitting right there in the folder. A missing choice should mean "the usual
 * one", never "silently use the worse renderer".
 */
export const defaultStyleFor = (kind: DecorStyle['kind']) =>
  DECOR_STYLES.find((s) => s.kind === kind && (s.segment ?? 'top') === 'top') ??
  DECOR_STYLES.find((s) => s.kind === kind)


/**
 * PALM PRESETS, named for the tree rather than for the sliders.
 *
 * Frond count, length and arch are three numbers nobody wants to dial from
 * scratch on every palm, and the differences between species are large: a
 * Christmas palm has a few short stiff fronds and a royal has a dozen long ones
 * arching most of the way back down. A contractor knows which palm he is
 * standing under; he does not know that it wants capDroop 0.9.
 *
 * Lengths are fractions of the photo width, which is how capLength is stored,
 * and are sized for a palm occupying a normal share of a front-of-house shot.
 */
export interface PalmPreset {
  id: string
  label: string
  fronds: number
  /** capLength: fraction of photo width. */
  length: number
  /** capDroop: 0 stiff, 1 heavy. */
  droop: number
}

export const PALM_PRESETS: PalmPreset[] = [
  { id: 'christmas', label: 'Christmas palm', fronds: 7, length: 0.035, droop: 0.25 },
  { id: 'queen', label: 'Queen palm', fronds: 11, length: 0.062, droop: 0.7 },
  { id: 'coconut', label: 'Coconut palm', fronds: 10, length: 0.075, droop: 0.55 },
  { id: 'royal', label: 'Royal palm', fronds: 13, length: 0.09, droop: 0.95 },
  { id: 'sabal', label: 'Sabal / cabbage', fronds: 16, length: 0.045, droop: 0.2 },
]
